// Spivex: paid add-ons as tick boxes on the product page (snippets/spivex-zusatz.liquid): the adapter in the vacuum
// check and the greeting card. Owner meeting and Tilmann's feedback 25.10.2026.
// - Ticked, the add-on's price leaves its row and is added to "In den Warenkorb · …" (.spivex-button-price).
// - "In den Warenkorb" adds the package, then every ticked add-on (the card with its text as a line item property).
// - While something is ticked the express buttons of the product page are hidden (class spivex-zusatz-an), because
//   Shopify's accelerated checkout only buys the product being viewed. The cart's express buttons buy all lines.
// Never preselected (§ 312a Abs. 3 BGB); in the vacuum check another answer clears the adapter tick.
// 27.10.2026: window.spivexZusaetzeHinzufuegen(root) for the Konfigurator's own cart button.
// 28.10.2026: after a ticked add-on is in the cart, the box gets the event spivex:zusatz-hinzugefuegt.
// 29.10.2026: vacuum check answer and adapter tick survive the package switch (fitStand).
// 28.10.2026: the greeting card gets "Beilegen zu: <package>" (shown in cart and order, sorts it under its set).
// Coming from the set finder's "Paket ansehen" (Tilmann 26.10.2026), the vacuum check opens on the answer with that
// adapter and the shopper's own ticks from there are set again, once (assets/spivex-set-finder.js).
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const BOX = 'input[data-spivex-zusatz]';
const TAKE_OVER = 'spivex-paket-zusaetze';
const TAKE_OVER_MAX_AGE = 30 * 60 * 1000;

/** @param {Element} element */
function rootOf(element) {
  return element.closest('.shopify-section') || document.body;
}

/** @param {HTMLInputElement} box */
function fieldsOf(box) {
  const form = box.dataset.spivexFelder ? document.getElementById(box.dataset.spivexFelder) : null;
  return form instanceof HTMLFormElement ? form : null;
}

/** @param {HTMLInputElement} box */
function priceOf(box) {
  const select = fieldsOf(box)?.querySelector('select[name="id"]');
  const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
  return Number(option?.dataset.spivexPreis || box.dataset.spivexPreis) || 0;
}

/** @param {Element} root */
function update(root) {
  /** @type {HTMLInputElement[]} */
  const ticked = [...root.querySelectorAll(`${BOX}:checked`)];
  root.classList.toggle('spivex-zusatz-an', ticked.length > 0);

  const extra = ticked.reduce((sum, box) => sum + priceOf(box), 0);
  // The button's price and, in the set finder, the big price above it ([data-spivex-gesamtpreis]).
  root.querySelectorAll('.spivex-button-price, [data-spivex-gesamtpreis]').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    element.dataset.spivexText ??= element.textContent || '';
    const base = Number(element.dataset.spivexPreis);
    const format = element.dataset.spivexGeldformat;
    if (!extra || !Number.isFinite(base) || !format) {
      element.textContent = element.dataset.spivexText;
      return;
    }
    element.textContent = formatMoney(base + extra, format, element.dataset.spivexWaehrung || '');
  });

  // Set finder: "Dein Spivex-Set … / Adapter … / Grußkarte …" above the total, so it is clear what it consists of.
  root.querySelectorAll('[data-spivex-aufstellung]').forEach((list) => {
    if (!(list instanceof HTMLElement)) return;
    list.querySelectorAll('[data-spivex-zeile]').forEach((row) => row.remove());
    const format = list.dataset.spivexGeldformat;
    if (format) {
      for (const box of ticked) {
        const row = document.createElement('div');
        const name = document.createElement('dt');
        const price = document.createElement('dd');
        row.dataset.spivexZeile = '';
        name.textContent = box.dataset.spivexKurzname || '';
        price.textContent = formatMoney(priceOf(box), format, list.dataset.spivexWaehrung || '');
        row.append(name, price);
        list.append(row);
      }
    }
    list.hidden = ticked.length === 0 || !format;
  });
}

/**
 * The set finder's choice for this package page: answer the vacuum check with the adapter's option, tick what the
 * shopper ticked there (adapter, greeting card with design and text).
 */
function takeOver() {
  /** @type {{path?: string, time?: number, adapter?: string | null, adapterTicked?: boolean, gift?: Record<string, string> | null} | null} */
  let data = null;
  try {
    data = JSON.parse(sessionStorage.getItem(TAKE_OVER) || 'null');
    sessionStorage.removeItem(TAKE_OVER);
  } catch {
    return;
  }
  if (!data || data.path !== window.location.pathname || !(Date.now() - (data.time || 0) < TAKE_OVER_MAX_AGE)) return;

  /** @type {Set<Element>} */
  const roots = new Set();
  // 29.10.2026: auch im Konfigurator („Set ändern“); dort gibt es den Check zweimal (mit/ohne Hülse), das
  // sichtbare Kreuz zuerst
  const passende = data.adapter
    ? [...document.querySelectorAll(`.spivex-fit ${BOX}[data-variant-id="${CSS.escape(data.adapter)}"]`)]
    : [];
  const adapter = passende.find((box) => !box.closest('[hidden], .spivex-konfig-aus') && box.closest('.spivex-fit')?.offsetParent !== null) || passende[0] || null;
  if (adapter instanceof HTMLInputElement) {
    const option = adapter
      .closest('.spivex-fit')
      ?.querySelector(`.spivex-fit__input[data-answer="${CSS.escape(adapter.dataset.answer || '')}"]`);
    if (option instanceof HTMLInputElement) option.checked = true;
    adapter.checked = Boolean(data.adapterTicked);
    window.setTimeout(() => {
      fitStand.answer = adapter.dataset.answer || null;
      fitStand.adapter = adapter.checked;
    });
    roots.add(rootOf(adapter));
  }

  const gift = data.gift ? document.querySelector('input.spivex-gift__box[data-spivex-zusatz]') : null;
  if (gift instanceof HTMLInputElement && data.gift) {
    const form = fieldsOf(gift);
    for (const [name, value] of Object.entries(data.gift)) {
      if (name === 'quantity') continue;
      const field = form?.querySelector(`[name="${CSS.escape(name)}"]`) ||
        (name.startsWith('properties[') ? form?.querySelector('textarea') : null);
      if (field instanceof HTMLSelectElement && ![...field.options].some((option) => option.value === value)) continue;
      if (field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) field.value = value;
    }
    gift.checked = true;
    roots.add(rootOf(gift));
  }

  roots.forEach(update);
}

takeOver();

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Vacuum check: the adapter tick belongs to its answer only.
  const fit = target.closest('.spivex-fit');
  if (fit) {
    const answer = fit.querySelector('.spivex-fit__input:checked')?.getAttribute('value');
    fit.querySelectorAll(BOX).forEach((box) => {
      if (box instanceof HTMLInputElement && box.dataset.answer !== answer) box.checked = false;
    });
    // 29.10.2026: merken für den Paketwechsel
    fitStand.answer = answer || null;
    fitStand.adapter = [...fit.querySelectorAll(`${BOX}:checked`)].some((box) => box instanceof HTMLInputElement);
  }

  const root = rootOf(target);
  if (root.querySelector(BOX)) update(root);
});

// 29.10.2026: Antwort im Staubsauger-Check und Adapter-Kreuz bleiben beim Paketwechsel auf der Produktseite erhalten
// (der Wechsel baut den Produktbereich neu auf und setzte beides zurück).
/** @type {{answer: string | null, adapter: boolean}} */
const fitStand = { answer: null, adapter: false };

/** @param {Element} root */
function fitWiederherstellen(root) {
  if (!fitStand.answer) return;
  root.querySelectorAll('.spivex-fit').forEach((fit) => {
    const option = fit.querySelector(`.spivex-fit__input[data-answer="${CSS.escape(fitStand.answer || '')}"]`);
    if (!(option instanceof HTMLInputElement)) return;
    option.checked = true;
    fit.querySelectorAll(BOX).forEach((box) => {
      if (!(box instanceof HTMLInputElement)) return;
      box.checked = fitStand.adapter && box.dataset.answer === fitStand.answer;
    });
  });
}

// The package picker swaps the product in place (assets/spivex-bundle-picker.js): new button, new ticks.
document.addEventListener('spivex:bundle-change', (event) => {
  if (!(event.target instanceof Element)) return;
  const root = rootOf(event.target);
  fitWiederherstellen(root);
  update(root);
  // falls der Austausch danach noch einmal etwas zurücksetzt
  requestAnimationFrame(() => {
    fitWiederherstellen(root);
    update(root);
  });
});

/**
 * Product title of a variant that is in the cart (for "Beilegen zu"), '' if not found.
 * @param {string | number} variantId
 */
async function paketTitelAusWarenkorb(variantId) {
  try {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json());
    const line = cart.items?.find((item) => String(item.variant_id) === String(variantId));
    return line?.product_title || '';
  } catch {
    return '';
  }
}

/**
 * Adds every ticked add-on in root (adapter, sleeve, greeting card with its text) after the package is in the cart.
 * Also used by the Konfigurator's own cart button (sections/spivex-konfigurator, 27.10.2026).
 * @param {Element} root
 */
async function addTicked(root) {
  if (typeof window.spivexInDenWarenkorb !== 'function') return;
  // 28.10.2026: the greeting card names the package it goes with ("Beilegen zu: Spivex Basis"). The package is
  // already in the cart here: its name comes from the cart (variant of the product form, in the Konfigurator from
  // its current choice).
  const paketFeld = root.querySelector('product-form-component input[name="id"], .product-form input[name="id"]');
  const paketId = (paketFeld instanceof HTMLInputElement && paketFeld.value) || window.spivexAktuelleAuswahl?.variantId;
  const hatKarte = [...root.querySelectorAll(`${BOX}:checked`)].some((box) => box.matches('[data-gk-box], .spivex-gift__box'));
  const paketTitel = paketId && hatKarte ? await paketTitelAusWarenkorb(paketId) : '';
  // erst wenn der Warenkorb mit dem Paket aufgegangen ist: zwei Änderungen direkt hintereinander unterbrechen sonst
  // die Animation des Warenkorbs (Fehler „Transition was skipped“, am iPhone hing danach teils die Seite)
  await new Promise((resolve) => setTimeout(resolve, 450));
  for (const box of root.querySelectorAll(`${BOX}:checked`)) {
    if (!(box instanceof HTMLInputElement)) continue;
    const form = fieldsOf(box);
    const status = box.dataset.spivexStatus ? document.getElementById(box.dataset.spivexStatus) : null;
    /** @type {Record<string, string>} */
    const eigenschaften = {};
    let variantId = box.dataset.variantId;
    if (form) {
      new FormData(form).forEach((value, name) => {
        const text = String(value).trim();
        const property = name.match(/^properties\[(.+)\]$/);
        if (name === 'id' && text) variantId = text;
        if (property && text) eigenschaften[property[1]] = text;
      });
    }
    if (paketTitel && box.matches('[data-gk-box], .spivex-gift__box') && !eigenschaften['Beilegen zu']) {
      eigenschaften['Beilegen zu'] = paketTitel;
    }
    try {
      await window.spivexInDenWarenkorb({ variantId, eigenschaften, ohneFlug: true, quelle: box });
      box.checked = false;
      form?.reset();
      if (status) status.textContent = box.dataset.successText || '';
      // Grußkarten-Motivwahl setzt sich danach zurück (assets/spivex-grusskarten.js, 28.10.2026)
      box.dispatchEvent(new CustomEvent('spivex:zusatz-hinzugefuegt', { bubbles: true }));
    } catch (error) {
      if (status) status.textContent = box.dataset.errorText || '';
    }
  }
  update(root);
}

window.spivexZusaetzeHinzufuegen = addTicked;

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || !event.promise) return;
  const root = event.target instanceof Element ? rootOf(event.target) : document.body;
  event.promise
    .then(async ({ detail }) => {
      if (!detail || detail.didError || detail.source !== 'product-form-component') return;
      await addTicked(root);
    })
    .catch(() => {});
});
